/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-non-null-asserted-optional-chain, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-enum-comparison, @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-unused-expressions, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */

import {
  GridSortItem,
  gridStringOrNumberComparator,
  gridNumberComparator,
  gridDateComparator,
  GridSortModel,
} from '@spotgamma/x-data-grid-premium';
import {
  FLOW_SUM_CATEGORIES,
  FLOW_SUM_CATEGORIES_VALUE_FORMAT,
  TAPE_DEFAULT_FILTER_ID,
  TAPE_FILTER_CONJUNCTION_ID,
} from 'config/tape';
import {
  OPTION_FEED_IDX,
  OptionFlag,
  OptionsFeedColumnKey,
  OptionTransactionType,
  RawOptionFeedData,
  OptionTradeSide,
  Filter,
  Aggressor,
  FilterConfig,
  FilterOperator,
  TransactionSentiment,
  ConjunctionOp,
  FilterItem,
  Conjunction,
  TnsFlowSummary,
  TnsSortedDataWindow,
  RawOptionFeedContract,
} from 'types/tape';
import { v4 as uuidv4 } from 'uuid';
import { dayjs, ET } from './shared/date';
import { OptionType } from 'types/shared';

const Mask = {
  OPTION_TYPE: 0x1, // 1 << 0
  TNS_TYPE: 0x6, // 3 << 1
  SIDE_TYPE: 0x18, // 3 << 3
  IS_NEXT_EXP: 0x20, // 1 << 5
  IS_RETAIL: 0x40, // 1 << 6
  IS_BLOCK: 0x80, // 1 << 7
  IS_SPREAD: 0x1_00, // 1 << 8
  IS_SWEEP: 0x2_00, // 1 << 9
  IS_CROSS: 0x8_00, // 1 << 11
  FILTERED_MASK: 0x40_00, // 1 << 14
};

const TYPE_STRS = [
  OptionTransactionType.NEW, // new
  OptionTransactionType.CORRECTION, // correction
  OptionTransactionType.CANCEL, // cancel
];

const typeToString = (tns_type: number): OptionTransactionType =>
  TYPE_STRS[tns_type];

const SIDE_STRS = [
  Aggressor.UNDETERMINED, // undetermined
  Aggressor.BUY, // buy
  Aggressor.SELL, // sell
];

const sideToString = (side_idx: number): Aggressor => SIDE_STRS[side_idx];

export const parseOptionFlags = (flags: number): OptionFlag => ({
  type: flags & Mask.OPTION_TYPE ? OptionType.PUT : OptionType.CALL,
  transactionType: typeToString((flags & Mask.TNS_TYPE) >> 1),
  sideType: sideToString((flags & Mask.SIDE_TYPE) >> 3),
  isNextExp: flags & Mask.IS_NEXT_EXP ? true : false,
  isRetail: flags & Mask.IS_RETAIL ? true : false,
  isBlock: flags & Mask.IS_BLOCK ? true : false,
  isSpread: flags & Mask.IS_SPREAD ? true : false,
  isSweep: flags & Mask.IS_SWEEP ? true : false,
  isCross: flags & Mask.IS_CROSS ? true : false,
});

const getSignedGamma = (gamma: number, sideType: Aggressor): number =>
  Math.abs(gamma) * (sideType === Aggressor.SELL ? -1 : 1);

const getSignedDelta = (
  delta: number,
  sideType: Aggressor,
  isPut: boolean,
): number => {
  const absDelta = Math.abs(delta);

  // Puts have negative delta by convention, calls are positive
  const directionalDelta = isPut ? -absDelta : absDelta;

  // Short positions flip the sign
  return directionalDelta * (sideType === Aggressor.SELL ? -1 : 1);
};

// rawSignal format: ['AAPL', 1718733518879n, 6553.737343480935, 228.74070754478853, 12244.064045890529, 213.61, 7381904254035821727n, 1737147600000n, 200, 1, 273, 'f', 7, 6.95, 7.05, 0.2009438256563837]
export const getOptionFeedDataRow = (rawSignal: any[]): RawOptionFeedData => {
  const flags = parseOptionFlags(rawSignal[OPTION_FEED_IDX.FLAG]);

  const optionSide = getOptionTradeSide(
    rawSignal[OPTION_FEED_IDX.PRICE],
    rawSignal[OPTION_FEED_IDX.BID],
    rawSignal[OPTION_FEED_IDX.ASK],
  );

  const signedGamma = getSignedGamma(
    rawSignal[OPTION_FEED_IDX.GAMMA],
    flags.sideType,
  );

  const signedDelta = getSignedDelta(
    rawSignal[OPTION_FEED_IDX.DELTA],
    flags.sideType,
    flags.type === OptionType.PUT,
  );

  return {
    [OptionsFeedColumnKey.Underlying]: rawSignal[OPTION_FEED_IDX.SYM],
    [OptionsFeedColumnKey.Time]: BigInt(rawSignal[OPTION_FEED_IDX.TIME]),
    [OptionsFeedColumnKey.Delta]: signedDelta,
    [OptionsFeedColumnKey.Gamma]: signedGamma,
    [OptionsFeedColumnKey.Vega]: rawSignal[OPTION_FEED_IDX.VEGA],
    [OptionsFeedColumnKey.StockPrice]: rawSignal[OPTION_FEED_IDX.STOCK_PRICE],
    [OptionsFeedColumnKey.DailyVolCumsum]: rawSignal[OPTION_FEED_IDX.DAILYVOL],
    [OptionsFeedColumnKey.PrevOi]: rawSignal[OPTION_FEED_IDX.PREVOI],
    [OptionsFeedColumnKey.Expiry]: BigInt(
      rawSignal[OPTION_FEED_IDX.EXPIRATION],
    ),
    [OptionsFeedColumnKey.Strike]: rawSignal[OPTION_FEED_IDX.STRIKE],
    [OptionsFeedColumnKey.Size]: rawSignal[OPTION_FEED_IDX.SIZE],
    [OptionsFeedColumnKey.Flags]: flags,
    [OptionsFeedColumnKey.ExchangeSaleCondition]:
      rawSignal[OPTION_FEED_IDX.EXCHANGE_SALE_CONDITIONS],
    [OptionsFeedColumnKey.Price]: rawSignal[OPTION_FEED_IDX.PRICE],
    [OptionsFeedColumnKey.Bid]: rawSignal[OPTION_FEED_IDX.BID],
    [OptionsFeedColumnKey.Ask]: rawSignal[OPTION_FEED_IDX.ASK],
    [OptionsFeedColumnKey.IVol]: rawSignal[OPTION_FEED_IDX.IVOL],
    [OptionsFeedColumnKey.Aggressor]: flags.sideType,
    [OptionsFeedColumnKey.IsPut]: flags.type === OptionType.PUT,
    [OptionsFeedColumnKey.TnsIndex]: BigInt(
      rawSignal[OPTION_FEED_IDX.TNS_INDEX],
    ),
    [OptionsFeedColumnKey.Premium]: rawSignal[OPTION_FEED_IDX.PREMIUM],
    [OptionsFeedColumnKey.TradeSide]: optionSide,
    [OptionsFeedColumnKey.IsBlock]: flags.isBlock,
    [OptionsFeedColumnKey.IsSpread]: flags.isSpread,
    [OptionsFeedColumnKey.IsSweep]: flags.isSweep,
    [OptionsFeedColumnKey.IsCross]: flags.isCross,
    id: uuidv4(),
    contractString: `${rawSignal[OPTION_FEED_IDX.SYM]}-${
      rawSignal[OPTION_FEED_IDX.STRIKE]
    }-${flags.type}-${rawSignal[OPTION_FEED_IDX.EXPIRATION]}-${
      rawSignal[OPTION_FEED_IDX.TNS_INDEX]
    }`,
  } as RawOptionFeedData;
};

const getOptionTradeSide = (
  optPrice: number,
  bidPrice: number,
  askPrice: number,
): OptionTradeSide => {
  if (optPrice < bidPrice) {
    return OptionTradeSide.BB;
  } else if (optPrice === bidPrice) {
    return OptionTradeSide.B;
  } else if (optPrice > bidPrice && optPrice < askPrice) {
    return OptionTradeSide.M;
  } else if (optPrice === askPrice) {
    return OptionTradeSide.A;
  }
  return OptionTradeSide.AA;
};

export const satisfiesDefaultConditions = (row: RawOptionFeedData) =>
  row[OptionsFeedColumnKey.Premium] > 1_000;

export const addOrUpdateFilters = (
  filters: Filter[],
  newFilters: Filter[],
): Filter[] => {
  const filterMap = new Map<string, Filter>(filters.map((f) => [f.id, f]));

  // Add or update new filters in the map
  newFilters.forEach((newFilter: Filter) => {
    const { id } = newFilter;

    if (isConjunction(newFilter)) {
      // If it's a conjunction, add or remove based on the number of filters it contains
      newFilter.filters.length > 0
        ? filterMap.set(id, newFilter)
        : filterMap.delete(id);
    } else {
      // It's a FilterItem; determine if it should be added or removed
      const { value } = newFilter;

      if (
        (Array.isArray(value) && value.length === 0) ||
        value === '' ||
        value == null ||
        value === 0
      ) {
        filterMap.delete(id);
      } else {
        filterMap.set(id, newFilter);
      }
    }
  });

  // Convert map back to array
  return Array.from(filterMap.values());
};

export const getFilter = (filterObj: any): FilterConfig => ({
  id: filterObj.filter_id,
  value: filterObj.filter,
  name: filterObj.name,
  noSym: filterObj.no_sym,
});

export const satisfyAllFilters = (
  data: RawOptionFeedData,
  filters: Filter[],
): boolean => filters.every((f) => satisfyFilter(data, f));

export const satisfyConjunction = (
  data: RawOptionFeedData,
  conjunction: Conjunction,
): boolean =>
  conjunction.op === ConjunctionOp.And
    ? conjunction.filters.every((f) => satisfyFilter(data, f))
    : conjunction.filters.some((f) => satisfyFilter(data, f));

export const satisfyFilter = (
  data: RawOptionFeedData,
  filter: Filter,
): boolean =>
  isConjunction(filter)
    ? satisfyConjunction(data, filter)
    : satisfyFilterItem(data, filter);

export const satisfyFilterItem = (
  data: RawOptionFeedData,
  filterItem: FilterItem,
): boolean => {
  const { field, value, operator } = filterItem;

  const dataValue = data[field as keyof RawOptionFeedData];

  // Handle Expiration field specifically
  if (
    filterItem.id === TAPE_DEFAULT_FILTER_ID.MinExp ||
    filterItem.id === TAPE_DEFAULT_FILTER_ID.MaxExp
  ) {
    // Convert the number of days to a BigInt timestamp for comparison
    const filterValue = daysToBigInt(value as number);
    return NUMERIC_COMPARISONS.get(operator)?.(
      dataValue as bigint,
      filterValue,
    )!;
  }

  if (filterItem.id === TAPE_DEFAULT_FILTER_ID.Expirations) {
    return handleEqualityAndArrayComparison(
      operator,
      dataValue as bigint,
      (value as string[]).map((v) => BigInt(parseInt(v))),
    );
  }

  // Convert dataValue and filter value to the same type before comparison
  const isDataValueBigInt = typeof dataValue === 'bigint';
  const isFilterValueBigInt = typeof value === 'bigint';

  if (isDataValueBigInt && !isFilterValueBigInt) {
    // Convert filter value to BigInt for comparison
    const bigIntFilterValue = BigInt(
      typeof value === 'string' ? parseInt(value) : (value as number),
    );
    return NUMERIC_COMPARISONS.get(operator)?.(
      dataValue as bigint,
      bigIntFilterValue,
    )!;
  }

  if (!isDataValueBigInt && isFilterValueBigInt) {
    // Convert data value to number for comparison
    const numberDataValue = Number(dataValue);
    return NUMERIC_COMPARISONS.get(operator)?.(
      numberDataValue,
      value as bigint,
    )!;
  }

  // Normal numeric or string comparisons
  if (
    typeof dataValue !== 'number' &&
    typeof dataValue !== 'string' &&
    typeof dataValue !== 'boolean'
  ) {
    return false;
  }

  if (
    (typeof dataValue === 'string' || typeof dataValue === 'boolean') &&
    dataValue === ''
  ) {
    return true;
  }

  if (typeof dataValue === 'number' || typeof dataValue === 'bigint') {
    return NUMERIC_COMPARISONS.get(operator)?.(
      dataValue,
      value as number | bigint,
    )!;
  }

  return handleEqualityAndArrayComparison(operator, dataValue, value);
};

const NUMERIC_COMPARISONS = new Map<
  FilterOperator,
  (a: bigint | number, b: bigint | number) => boolean
>([
  [FilterOperator.LessThan, (a, b) => a < b],
  [FilterOperator.LessThanOrEqual, (a, b) => a <= b],
  [FilterOperator.GreaterThan, (a, b) => a > b],
  [FilterOperator.GreaterThanOrEqual, (a, b) => a >= b],
]);

const handleEqualityAndArrayComparison = (
  operator: FilterOperator,
  dataValue: string | boolean | number | bigint,
  filterValue: FilterItem['value'],
): boolean => {
  if (operator === FilterOperator.Equal) {
    return dataValue === filterValue;
  } else if (operator === FilterOperator.NotEqual) {
    return dataValue !== filterValue;
  } else if (
    operator === FilterOperator.IsAnyOf &&
    Array.isArray(filterValue)
  ) {
    // Check the type of the first element to decide whether to cast to string[] or number[]
    if (typeof dataValue === 'string') {
      let lowerDataValue = dataValue.toLowerCase();
      const endIdx = lowerDataValue.lastIndexOf('|');
      lowerDataValue =
        endIdx < 0 ? lowerDataValue : lowerDataValue.substring(0, endIdx);

      return (filterValue as string[]).some(
        (filterStr) => lowerDataValue === filterStr.toLowerCase(),
      );
    } else if (typeof dataValue === 'number') {
      return (filterValue as number[]).includes(dataValue);
    } else if (typeof dataValue === 'bigint') {
      return (filterValue as bigint[]).includes(dataValue);
    }
  }

  return true;
};

const daysToBigInt = (days: number): bigint => {
  const now = BigInt(Date.now());
  const daysInt = BigInt(Math.round(days)); // Ensure days is an big int
  return now + daysInt * (1_000n * 60n * 60n * 24n); // milliseconds in a day
};

export const getTransactionSentiment = (
  row: RawOptionFeedData,
): TransactionSentiment => {
  const isBullish =
    (row[OptionsFeedColumnKey.Aggressor] === Aggressor.BUY &&
      (row[OptionsFeedColumnKey.TradeSide] === OptionTradeSide.AA ||
        row[OptionsFeedColumnKey.TradeSide] === OptionTradeSide.A) &&
      row[OptionsFeedColumnKey.IsPut] === false) ||
    (row[OptionsFeedColumnKey.Aggressor] === Aggressor.SELL &&
      (row[OptionsFeedColumnKey.TradeSide] === OptionTradeSide.BB ||
        row[OptionsFeedColumnKey.TradeSide] === OptionTradeSide.B) &&
      row[OptionsFeedColumnKey.IsPut] === true);

  const isBearish =
    (row[OptionsFeedColumnKey.Aggressor] === Aggressor.BUY &&
      (row[OptionsFeedColumnKey.TradeSide] === OptionTradeSide.AA ||
        row[OptionsFeedColumnKey.TradeSide] === OptionTradeSide.A) &&
      row[OptionsFeedColumnKey.IsPut] === true) ||
    (row[OptionsFeedColumnKey.Aggressor] === Aggressor.SELL &&
      (row[OptionsFeedColumnKey.TradeSide] === OptionTradeSide.BB ||
        row[OptionsFeedColumnKey.TradeSide] === OptionTradeSide.B) &&
      row[OptionsFeedColumnKey.IsPut] === false);

  return isBullish ? 'bullish' : isBearish ? 'bearish' : 'neutral';
};

// Utility to get filter value or return default
export const getFilterValue = <T>(
  filters: Filter[],
  filterId: TAPE_DEFAULT_FILTER_ID,
  defaultValue: T,
): T => {
  const filter = filters.find((f) => f.id === filterId) as FilterItem;
  return (filter?.value as T) ?? defaultValue;
};

export const getFilterConjunction = <Conjunction>(
  filters: Filter[],
  cId: TAPE_FILTER_CONJUNCTION_ID,
  defaultValue: Conjunction,
): Conjunction => {
  const filter = filters.find((f) => f.id === cId) as Conjunction;
  return filter ?? defaultValue;
};

export const isConjunction = (filter: Filter): filter is Conjunction =>
  'op' in filter;

export const getActiveFilterCount = (filters: Filter[]): number =>
  filters.filter((f) =>
    isConjunction(f)
      ? f.filters.length > 0
      : f.value != null &&
        f.value !== '' &&
        !(Array.isArray(f.value) && f.value.length === 0),
  ).length;

export const optionsFeedComparator = <
  T extends RawOptionFeedData | RawOptionFeedContract,
>(
  a: T,
  b: T,
  sortItem: GridSortItem,
) => {
  const { field, sort } = sortItem;
  const valueA = a[field as keyof T];
  const valueB = b[field as keyof T];

  const dir = sort === 'asc' ? 1 : -1;

  if (typeof valueA === 'string' && typeof valueB === 'string') {
    return (
      gridStringOrNumberComparator(valueA, valueB, {} as any, {} as any) * dir
    );
  } else if (
    (typeof valueA === 'number' || typeof valueA === 'bigint') &&
    (typeof valueB === 'number' || typeof valueB === 'bigint')
  ) {
    return gridNumberComparator(valueA, valueB, {} as any, {} as any) * dir;
  } else if (valueA instanceof Date && valueB instanceof Date) {
    return gridDateComparator(valueA, valueB, {} as any, {} as any) * dir;
  }
  return 0;
};

export const multiSortComparator = <
  T extends RawOptionFeedData | RawOptionFeedContract,
>(
  a: T,
  b: T,
  sortItems: GridSortItem[],
): number => {
  for (const sortItem of sortItems) {
    const comparison = optionsFeedComparator(a, b, sortItem);
    if (comparison !== 0) {
      return comparison;
    }
  }
  return 0;
};

const DEFAULT_TIME_SORT: GridSortItem = {
  field: OptionsFeedColumnKey.Time,
  sort: 'desc', // Most recent first
};

export const getSortedOptionsFeedData = <
  T extends RawOptionFeedData | RawOptionFeedContract,
>(
  data: T[],
  sortModel: GridSortModel,
): T[] => {
  // Create a complete sort model that includes the time sort
  const completeSortModel = [...sortModel];

  // Only add time sort if it's not already included
  const hasTimeSort = completeSortModel.some(
    (item) => item.field === OptionsFeedColumnKey.Time,
  );

  if (!hasTimeSort) {
    completeSortModel.push(DEFAULT_TIME_SORT);
  }

  return [...data].sort((a, b) => multiSortComparator(a, b, completeSortModel));
};

export const makeTnsFlowSummary = (rawFlowSumData: any): TnsFlowSummary => {
  // Initialize an empty object to build TnsFlowSummary
  const flowSum: Partial<TnsFlowSummary> = {};

  // Iterate over each category to populate flowSum
  FLOW_SUM_CATEGORIES.forEach((category) => {
    const data = rawFlowSumData[category];

    if (data) {
      const { put, call } = data;

      // Assign the transformed PCFlowSum with 'total'
      flowSum[category] = {
        put: put,
        call: call,
        total: (put ?? 0) + (call ?? 0),
        format: FLOW_SUM_CATEGORIES_VALUE_FORMAT.get(category),
      };
    }
  });

  // Type assertion since we've ensured all categories are present
  return flowSum as TnsFlowSummary;
};

// function to handle conversion of value types backend expects
export const getFiltersForPayload = (filters: Filter[]) =>
  filters.map((f: Filter) => {
    if (!isConjunction(f)) {
      if (
        f.id === TAPE_DEFAULT_FILTER_ID.MinExp ||
        f.id === TAPE_DEFAULT_FILTER_ID.MaxExp
      ) {
        const fromDateTimeFilter = filters.find(
          (filter) =>
            !isConjunction(filter) &&
            filter.id === TAPE_DEFAULT_FILTER_ID.MinDateTime,
        ) as FilterItem;
        return {
          ...f,
          value: Math.round(
            daysToTimestamp(
              f.value as number,
              fromDateTimeFilter?.value as number,
            ),
          ),
        };
      } else if (f.id === TAPE_DEFAULT_FILTER_ID.Expirations) {
        return {
          ...f,
          value: (f.value as string[]).map((str) => parseInt(str, 10)),
        };
      }
      // add more cases as necessary
    }

    return f;
  });

const daysToTimestamp = (days: number, referenceDate?: number): number => {
  const baseDate = referenceDate != null ? dayjs(referenceDate) : dayjs();
  return baseDate
    .tz(ET)
    .startOf('day')
    .add(days, 'days')
    .set('hour', 16)
    .set('minute', 0)
    .set('second', 0)
    .set('millisecond', 0)
    .valueOf();
};

/**
 * Deduplicates two arrays of rows based on the 'contractString' field.
 * Prioritizes rows from liveRows over histRows.
 *
 * @param {(RawOptionFeedData | RawOptionFeedContract)[]} liveRows - Rows from WebSocket (live data).
 * @param {(RawOptionFeedData | RawOptionFeedContract)[]} histRows - Rows from historical data.
 * @returns {(RawOptionFeedData | RawOptionFeedContract)[]} - Deduplicated array of rows.
 */
export const dedupeTnsRows = <
  T extends RawOptionFeedData | RawOptionFeedContract,
>(
  liveRows: T[],
  histRows: T[],
): T[] => {
  const seenIds = new Set<string>();
  const deduped: T[] = [];

  // Add live rows first
  for (const row of liveRows) {
    if (row.contractString && !seenIds.has(row.contractString)) {
      seenIds.add(row.contractString);
      deduped.push(row);
    }
  }

  // Add historical rows, skipping duplicates
  for (const row of histRows) {
    if (row.contractString && !seenIds.has(row.contractString)) {
      seenIds.add(row.contractString);
      deduped.push(row);
    }
  }

  return deduped;
};

export const fitsTnsSortedWindow = (
  row: RawOptionFeedData,
  sortedDataWindow: TnsSortedDataWindow,
): boolean => {
  const { sortModel, firstRow, lastRow } = sortedDataWindow;

  // If no sort model, we can't determine if it fits
  if (sortModel.length === 0 || !firstRow || !lastRow) {
    return false;
  }

  // Create complete sort model with time sort
  const completeSortModel = [...sortModel];
  if (
    !completeSortModel.some((item) => item.field === OptionsFeedColumnKey.Time)
  ) {
    completeSortModel.push(DEFAULT_TIME_SORT);
  }

  // Compare with first and last rows using all sort criteria
  const firstRowComparison = multiSortComparator(
    row,
    firstRow,
    completeSortModel,
  );
  const lastRowComparison = multiSortComparator(
    row,
    lastRow,
    completeSortModel,
  );

  // For ascending sort:
  // - row should be greater than or equal to firstRow (firstRowComparison >= 0)
  // - row should be less than or equal to lastRow (lastRowComparison <= 0)
  // For descending sort:
  // - row should be less than or equal to firstRow (firstRowComparison <= 0)
  // - row should be greater than or equal to lastRow (lastRowComparison >= 0)
  return sortModel[0].sort === 'asc'
    ? firstRowComparison >= 0 && lastRowComparison <= 0
    : firstRowComparison <= 0 && lastRowComparison >= 0;
};

export const getOptionFeedContract = (
  data: RawOptionFeedContract,
): RawOptionFeedContract => ({
  ...data,
  id: uuidv4(),
  contractString: `${data[OptionsFeedColumnKey.Underlying]}_${
    data[OptionsFeedColumnKey.Strike]
  }_${data[OptionsFeedColumnKey.Expiry]}_${data[OptionsFeedColumnKey.IsPut]}`,
});

export const validateTnsRow = (
  row: RawOptionFeedData,
  filters: Filter[],
  sortedWindow?: TnsSortedDataWindow,
): boolean => {
  // First check if row passes filters
  if (!satisfyAllFilters(row, filters)) {
    return false;
  }

  // validate sort window
  return validateSortWindow(row, sortedWindow);
};

export const validateSortWindow = (
  row: RawOptionFeedData,
  sortedWindow?: TnsSortedDataWindow,
) => {
  // If we have a sort window with all required properties, check if row fits
  if (
    sortedWindow?.sortModel &&
    sortedWindow?.sortModel.length > 0 &&
    sortedWindow?.sortModel[0].field !== OptionsFeedColumnKey.Time &&
    sortedWindow?.sortModel[0].sort !== 'desc' &&
    sortedWindow?.firstRow &&
    sortedWindow?.lastRow
  ) {
    return fitsTnsSortedWindow(row, sortedWindow);
  }
  // If no sort window or incomplete window properties, accept the row
  return true;
};

export const getExpirationFilterItems = (value: string[]) => [
  {
    id: TAPE_DEFAULT_FILTER_ID.Expirations,
    field: OptionsFeedColumnKey.Expiry,
    operator: FilterOperator.IsAnyOf,
    value,
  },
  {
    id: TAPE_DEFAULT_FILTER_ID.MinExp,
    field: OptionsFeedColumnKey.Expiry,
    operator: FilterOperator.GreaterThanOrEqual,
    value: null, // pass null to reset expiration slider
  },
  {
    id: TAPE_DEFAULT_FILTER_ID.MaxExp,
    field: OptionsFeedColumnKey.Expiry,
    operator: FilterOperator.LessThanOrEqual,
    value: null, // pass null to reset expiration slider
  },
];
